/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const fallbackWebButtonTypography = {
    fontSize: {base: 'webMd'},
    lineHeight: {base: '20px'},
    fontWeight: 'bold',
    textTransform: 'uppercase'
}

const getWebButtonTypography = (props) => ({
    ...fallbackWebButtonTypography,
    ...(props?.theme?.textStyles?.webButtonTypography || {})
})

// Disable hover/active styles on touch devices. Touch devices report
// `hover: none` so the @media block is skipped, preventing sticky states
// after a tap. Desktop (hover: hover) keeps normal hover/active feedback.
//
// We use `_hover`/`_active` as the outer key (so Chakra expands them to
// &:hover / &:active) and nest the @media query inside. Emotion hoists
// nested @media to the top level automatically. This avoids key collisions
// that would happen if both helpers produced the same '@media (hover: hover)'
// top-level key (JS objects can't have duplicate keys — the second would
// silently overwrite the first).
const hoverOnly = (styles) => ({
    _hover: {
        '@media (hover: hover)': styles
    }
})

const activeOnly = (styles) => ({
    _active: {
        '@media (hover: hover)': styles
    }
})

// `--chakra-shadows-outline` is redefined in theme/foundations/styles.js. Raw theme
// tokens (e.g. `webTheme.focus`) are not resolved inside multi-part boxShadow values,
// which produces invalid CSS and removes the focus ring entirely.
const webFocusVisible = {
    boxShadow: 'var(--chakra-shadows-outline)'
}

export default {
    baseStyle: {
        borderRadius: 'none',
        selectedSortLink: {
            position: 'relative',
            cursor: 'auto',
            _before: {
                content: '""',
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M16.6634 5L7.49674 14.1667L3.33008 10' stroke='black' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat center center/20px;`,
                width: '20px',
                height: '20px'
            }
        },
        parts: ['selectedSortLink']
    },
    variants: {
        solid: (props) => ({
            backgroundColor: 'primaryBlack',
            color: 'white',
            textTransform: 'uppercase',
            fontSize: 'sm',
            fontWeight: '700',
            ...hoverOnly({
                bg: 'charcoal',
                textDecoration: 'none',
                _disabled: {
                    background: 'primaryBlack'
                }
            }),
            _loading: {
                backgroundColor: 'primaryBlack',
                opacity: 1,
                
                _hover: {
                    backgroundColor: 'primaryBlack',
                    opacity: 1,
                }
            },
            ...activeOnly({bg: 'charcoal'})
        }),
        secondary: (props) => ({
            backgroundColor: 'white',
            padding: 2,
            color: 'black',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            position: 'relative',
            ...hoverOnly({
                bg: 'gray.800'
            })
        }),
        quickViewBtn: () => ({
            backgroundColor: 'webButton.btnPrimaryReverseDefault',
            padding: '8px 24px',
            color: 'webTheme.textOnColor',
            position: 'relative',
            height: {base: 'auto', lg: 10},
            ...hoverOnly({
                backgroundColor: 'webButton.btnPrimaryReverseDefault'
            })
        }),
        accountlink: {
            fontSize: 'md',
            fontWeight: 'normal',
            color: 'black',
            justifyContent: 'flex-start'
        },
        accountDashboardlink: (props) => ({
            justifyContent: 'space-between',
            borderRadius: 'none',
            background: 'transparent',
            color: props.isActive ? 'black' : 'gray.700',
            padding: 0,
            fontSize: {base: 'webLg', lg: 'webMd'},
            fontWeight: props.isActive ? 'medium' : 'normal',
            height: 'auto',
            margin: 0
        }),
        closeAccountLink: {
            color: 'webTypo.textPrimary',
            fontWeight: 'bold',
            textDecoration: 'underline',
            textTransform: 'uppercase',
            fontSize: 'sm',
            padding: 0,
            height: 'auto',
            ...hoverOnly({
                color: 'webTypo.textPrimary'
            }),
            _focusVisible: {
                color: 'webTypo.textPrimary'
            }
        },
        outline: (props) => ({
            color: 'black',
            fontSize: 'sm',
            backgroundColor: 'white',
            borderWidth: '1px',
            borderColor: props.colorScheme === 'black' ? 'gray.50' : 'black',
            textTransform: 'uppercase',
            ...hoverOnly({
                bg: props.colorScheme === 'black' ? 'black' : 'gray.50'
            })
        }),
        outlineCircle: (props) => ({
            color: 'black',
            fontSize: 'sm',
            backgroundColor: 'white',
            borderWidth: '0',
            width: {base: props.isProductTile ? '12px' : '44px', lg: '20px'},
            minWidth: {base: props.isProductTile ? '12px' : '44px', lg: '20px'},
            height: {base: props.isProductTile ? '12px' : '44px', lg: '20px'},
            textTransform: 'uppercase',
            padding: {base: props.isProductTile ? '0' : '10px', lg: '2px'},
            ...hoverOnly({
                bg: props.colorScheme === 'black' ? 'black' : 'white'
            })
        }),
        socialLogin: (props) => ({
            color: 'black',
            fontSize: 'xs',
            fontWeight: '400',
            backgroundColor: 'white',
            minHeight: '48px',
            borderWidth: '1px',
            borderColor: props.colorScheme === 'black' ? 'gray.50' : 'black',
            width: '100%',
            ...hoverOnly({
                bg: props.colorScheme === 'black' ? 'black' : 'gray.50'
            })
        }),
        footer: {
            fontSize: 'sm',
            backgroundColor: 'gray.100',
            color: 'black',
            ...hoverOnly({bg: 'gray.200'}),
            ...activeOnly({bg: 'gray.300'}),
            paddingLeft: 3,
            paddingRight: 3
        },
        tabButton: (props) => ({
            display: 'inline-flex',
            paddingY: 2,
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: props.isActive ? 'black' : 'gray.50',
            borderRadius: '0',
            bg: 'transparent',
            cursor: 'pointer',
            color: 'black',
            fontSize: 'sm',
            fontWeight: 'normal',
            flex: 1,
            _hover: {opacity: 0.8},
            height: '36px'
        }),
        link: (props) =>
            props.colorScheme === 'white'
                ? {
                      color: 'white',
                      fontWeight: 'normal',
                      textDecoration: 'underline',
                      minWidth: '1em',
                      lineHeight: 4,
                      ...hoverOnly({
                          color: 'white'
                      }),
                      _focusVisible: {
                          color: 'white'
                      }
                  }
                : {
                      color: 'black',
                      fontWeight: 'normal',
                      textDecoration: 'underline',
                      minWidth: '1em',
                      lineHeight: 4,
                      ...hoverOnly({
                          color: 'black'
                      }),
                      _focusVisible: {
                          color: 'black'
                      }
                  },
        clear: {
            color: 'webTypo.textTertiary',
            fontSize: '12px',
            fontWeight: '400',
            lineHeight: '18px',
            cursor: 'pointer',
            padding: 0,
            height: 'auto',
            minWidth: 0,
            textDecoration: 'underline',
            _hover: {textDecoration: 'underline', color: 'webTypo.textPrimary'}
        },
        'menu-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            ...hoverOnly({
                bg: 'gray.50',
                textDecoration: 'underline',
                textDecorationColor: 'gray.900'
            }),
            _activeLink: {
                bg: 'gray.50',
                borderLeft: 'solid',
                borderLeftColor: 'gray.600',
                borderLeftWidth: '4px'
            }
        },
        'menu-link-mobile': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            ...hoverOnly({
                bg: 'gray.50',
                textDecoration: 'underline',
                textDecorationColor: 'gray.900'
            }),
            _activeLink: {
                bg: 'gray.100',
                border: 'solid',
                borderColor: 'gray.600',
                borderWidth: '1px'
            }
        },
        'search-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            ...hoverOnly({textDecoration: 'none'})
        },
        'search-suggestion': {
            color: 'black',
            fontWeight: 'normal',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            alignItems: 'center',
            gap: 1,
            px: 0,
            py: 1,
            height: 'auto',
            minHeight: 0,
            minWidth: 0,
            _hover: {textDecoration: 'none', opacity: 0.7}
        },
        'quantity-picker': {
            color: 'black',
            fontSize: 'sm',
            padding: 0,
            height: 'auto',
            minWidth: '20px',
            fontWeight: '400',
            _disabled: {
                opacity: 1
            }
        },
        filter: (props) => ({
            textStyle: 'labelSmMd',
            display: 'flex',
            height: 'auto',
            minHeight: '24px',
            padding: '0 8px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            flexShrink: 0,
            borderColor: 'webTheme.strokeDefault',
            border: '1px solid',
            ...hoverOnly({
                background: 'black',
                color: 'white'
            })
        }),
        filterLink: {
            color: 'black',
            fontSize: 'md',
            fontWeight: 500,
            borderBottom: '1px solid',
            borderColor: 'gray.50',
            paddingLeft: '32px',
            paddingRight: '32px',
            paddingTop: '20px',
            paddingBottom: '20px',
            height: 'auto'
        },
        selectedFilter: {
            background: 'webButton.btnPrimaryDefault',
            display: 'flex',
            height: 'auto',
            minHeight: '24px',
            padding: '0 8px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1
        },
        sortSelect: {
            display: 'inline-flex',
            height: '24px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
            paddingLeft: 0,
            paddingRight: 0
        },
        sortLink: (props) => ({
            ...(props?.theme?.textStyles?.bodySmRegular || {}),
            height: 'auto',
            borderBottom: '1px solid',
            borderColor: 'webTheme.strokeTertiary',
            paddingTop: '14px',
            paddingBottom: '14px',
            paddingLeft: '8px',
            paddingRight: '8px',
            ...hoverOnly({
                background: 'webTheme.fieldHover'
            })
        }),
        webPrimary: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnPrimaryDefault',
            color: 'webTypo.textPrimaryReverse',
            minHeight: '48px',
            ...hoverOnly({
                backgroundColor: 'webButton.btnPrimaryHover',
                _disabled: {
                    backgroundColor: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({backgroundColor: 'webButton.btnPrimaryHover'}),
            _loading: {
                backgroundColor: 'webButton.btnPrimaryDefault',
                _hover: {
                    backgroundColor: 'webButton.btnPrimaryDefault'
                }
            },
            _disabled: {
                backgroundColor: 'webButton.btnPrimaryDisabled',
                color: 'webTypo.textPrimaryReverse',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutline: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnSecondaryDefault',
            color: 'webTypo.textPrimary',
            height: '48px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimary',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse',
                _disabled: {
                    backgroundColor: 'webButton.btnSecondaryDisabled',
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse'
            }),
            _disabled: {
                backgroundColor: 'webButton.btnSecondaryDisabled',
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutlineTransparent: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webTypo.textPrimary',
            height: '48px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimary',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse',
                _disabled: {
                    backgroundColor: 'transparent',
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse'
            }),
            _disabled: {
                backgroundColor: 'transparent',
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1,
                pointerEvents: 'none'
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutlineTransparentSmall: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webTypo.textPrimary',
            height: 'auto',
            minHeight: '40px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimary',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse',
                _disabled: {
                    backgroundColor: 'transparent',
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse'
            }),
            _disabled: {
                backgroundColor: 'transparent',
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1,
                pointerEvents: 'none'
            },
            _focusVisible: webFocusVisible
        }),
        webPrimarySmall: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnPrimaryDefault',
            color: 'webTypo.textPrimaryReverse',
            height: 'auto',
            minHeight: '40px',
            ...hoverOnly({
                backgroundColor: 'webButton.btnPrimaryHover',
                _disabled: {
                    backgroundColor: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({backgroundColor: 'webButton.btnPrimaryHover'}),
            _loading: {
                backgroundColor: 'webButton.btnPrimaryDefault',
                _hover: {
                    backgroundColor: 'webButton.btnPrimaryDefault'
                }
            },
            _disabled: {
                backgroundColor: 'webButton.btnPrimaryDisabled',
                color: 'webTypo.textPrimaryReverse',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutlineSmall: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnSecondaryDefault',
            color: 'webTypo.textPrimary',
            height: 'auto',
            minHeight: '40px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimary',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse',
                _disabled: {
                    backgroundColor: 'webButton.btnSecondaryDisabled',
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnSecondaryHover',
                color: 'webTypo.textPrimaryReverse'
            }),
            _disabled: {
                backgroundColor: 'webButton.btnSecondaryDisabled',
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webTertiary: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webButton.btnTertiaryTextDefault',
            height: 'auto',
            minWidth: 'auto',
            padding: 0,
            textDecoration: 'underline',
            _disabled: {
                color: 'webButton.btnTertiaryTextDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webPrimaryReverse: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnPrimaryReverseDefault',
            color: 'webTypo.textPrimary',
            height: '48px',
            ...hoverOnly({
                backgroundColor: 'webButton.btnPrimaryReverseHover',
                _disabled: {
                    backgroundColor: 'webButton.btnPrimaryReverseDisabled'
                }
            }),
            ...activeOnly({backgroundColor: 'webButton.btnPrimaryReverseHover'}),
            _loading: {
                backgroundColor: 'webButton.btnPrimaryReverseDefault',
                _hover: {
                    backgroundColor: 'webButton.btnPrimaryReverseDefault'
                }
            },
            _disabled: {
                backgroundColor: 'webButton.btnPrimaryReverseDisabled',
                color: 'webTypo.textPrimary',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutlineReverse: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webTypo.textPrimaryReverse',
            height: '48px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimaryReverse',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryDefault',
                color: 'webTypo.textPrimary',
                _disabled: {
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnPrimaryReverseHover',
                color: 'webTypo.textPrimary'
            }),
            _disabled: {
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webPrimaryReverseSmall: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'webButton.btnPrimaryReverseDefault',
            color: 'webTypo.textPrimary',
            height: '40px',
            ...hoverOnly({
                backgroundColor: 'webButton.btnPrimaryReverseHover',
                _disabled: {
                    backgroundColor: 'webButton.btnPrimaryReverseDisabled'
                }
            }),
            ...activeOnly({backgroundColor: 'webButton.btnPrimaryReverseHover'}),
            _loading: {
                backgroundColor: 'webButton.btnPrimaryReverseDefault',
                _hover: {
                    backgroundColor: 'webButton.btnPrimaryReverseDefault'
                }
            },
            _disabled: {
                backgroundColor: 'webButton.btnPrimaryReverseDisabled',
                color: 'webTypo.textPrimary',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webSecondaryOutlineReverseSmall: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webTypo.textPrimaryReverse',
            height: '40px',
            borderWidth: '1px',
            borderColor: 'webTypo.textPrimaryReverse',
            ...hoverOnly({
                backgroundColor: 'webButton.btnSecondaryDefault',
                color: 'webTypo.textPrimary',
                _disabled: {
                    borderColor: 'webButton.btnPrimaryDisabled',
                    color: 'webButton.btnPrimaryDisabled'
                }
            }),
            ...activeOnly({
                backgroundColor: 'webButton.btnPrimaryReverseHover',
                color: 'webTypo.textPrimary'
            }),
            _disabled: {
                borderColor: 'webButton.btnPrimaryDisabled',
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        webTertiaryLinkReverse: (props) => ({
            ...getWebButtonTypography(props),
            backgroundColor: 'transparent',
            color: 'webTypo.textPrimaryReverse',
            height: 'auto',
            minWidth: 'auto',
            padding: 0,
            textDecoration: 'underline',
            _disabled: {
                color: 'webButton.btnPrimaryDisabled',
                opacity: 1
            },
            _focusVisible: webFocusVisible
        }),
        parts: ['sortLink', 'selectedSortLink']
    },
    sizes: {
        md: {
            height: 11,
            minWidth: 11
        }
    },
    defaultProps: {
        colorScheme: 'blue'
    }
}
